import React from 'react';
import { connect } from 'react-redux';
import { fetchEventDetails } from '../../actions/event';
import { fetchResponses } from '../../actions/responses';
import Logger from '../../util/Logger';
import ErrorAndRefresh from '../shared/ErrorAndRefresh';
import LoadingAndPulse from '../shared/LoadingAndPulse';
import { NETWORK_CODE_NOT_FOUND } from '../../constants';
import { handleError } from '../../util/Errors';
import Event from '../../models/Event';
import './QuestionStats.css';
import iconUsers from '../../images/event_stats_users.png';
import iconContributions from '../../images/event_stats_contributions.png';
import iconComments from '../../images/comments-small.svg';
import iconParticipants from '../../images/participants.png';
import iconVotes from '../../images/votes.png';
import iconResponses from '../../images/check.png';
import EventStats from '../../models/EventStats';
import { EventVisibilityType } from '../../models/EventVisibilityType';
import { EventQuestionType } from '../../models/EventQuestionType';
import EmptyState from '../shared/EmptyState';
import Tooltip from '../shared/Tooltip';

class QuestionStats extends React.Component {

  constructor (props) {
    super(props);

    const { event, user } = props;
    if (!user) {
      Logger.assert('Missing user in QuestionStats component');
    }

    if (!event) {
      Logger.assert('Missing event in QuestionStats component');
    }
  }

  componentDidUpdate (prevProps) {
    const { error } = this.props;
    handleError(error, prevProps.error);
  }

  loadStats = () => {
    const { fetchEventDetails, fetchResponses, event } = this.props;
    fetchEventDetails(Event.getId(event));
    fetchResponses(Event.getId(event));
  };

  render () {
    const { loading, error, stats, event } = this.props;
    const questionType = Event.getQuestionType(event);
    const cardType = Event.getVisibilityType(event) === EventVisibilityType.INVITE_ONLY
      ? 'invite'
      : 'anybody';
    const questionStatsCardClass = EventQuestionType.isOpenEndedType(questionType)
      ? `question-stats-large-${cardType}`
      : `question-stats-small-${cardType}`;
    if (error) {
      return (
        <div className={ `${questionStatsCardClass}` }>
          <ErrorAndRefresh
            error="Unable to load Event"
            // If its a 404, hide the refresh button cause it doesn't exist anyway.
            onRefresh={ error.code === NETWORK_CODE_NOT_FOUND ? null : this.loadStats } />
        </div>
      );
    } else if (loading || !stats) {
      // Else if we don't have stats or we are still loading
      return (
        <div className={ `${questionStatsCardClass}` }>
          <LoadingAndPulse
            size={ 20 }
            color="#3D9AE2"
            whatIsLoading="Question Stats" />
        </div>
      );
    } else {
      // Map the returned server data into visual data
      const viewerCount = EventStats.getReachCount(stats);

      // If null, the server was not tracking viewer count so we hide the item
      const leftItem1 = QuestionStatsLeftItem(
        iconUsers,
        viewerCount === 1 ? 'View' : 'Views',
        viewerCount,
        'All users who viewed your POPin.'
      );

      const rightItemList1 = [];
      // Comment out invited: https://trello.com/c/xixLYEID/32-comment-out-invited-on-creator-report-csv-export
      // const type = Event.getVisibilityType(event);
      // if (EventVisibilityType.INVITE_ONLY === type) {
      //   rightItemList1.push(
      //     QuestionStatsRightItem('Invited', null, EventStats.getUsersWhoWereInvitedCount(stats)));
      // }

      // Take out views:   https://trello.com/c/XneTyktq/1482-rename-and-hide-info-on-creator-report
      // Server doesn't always correctly bump this number?
      // As long as a participant sees the page_view for participant response list, it should increment
      // rightItemList1.push(
      //   QuestionStatsRightItem('Views', null, EventStats.getViewsCount(stats)));

      rightItemList1.push(
        QuestionStatsRightItem(
          'Active PARTICIPANTS',
          null,
          EventStats.getUsersWhoContributedCount(stats),
          'All users who participated to your POPin.',
          <i className="material-icons" style={{fontSize: 36, color: '#76c1df'}}>people_alt</i>,
        ),
      );

      const leftItem2 = QuestionStatsLeftItem(iconContributions, 'Contributions',
        EventStats.getTotalContributionsCount(stats),
        'A contribution is a user response, comment, or vote.');

      let voteCount = 0;
      const responderCount = EventStats.getResponderCount(stats);
      if (EventQuestionType.isOpenEndedType(questionType)) {
        rightItemList1.push(QuestionStatsRightItem(
          `RESPONSES`,
          responderCount,
          EventStats.getResponseCount(stats),
          'All user responses.',
          <i className="material-icons" style={{fontSize: 36, color: '#ffce75'}}>chat_bubble</i>,
        ));
        voteCount = EventStats.getResponseVoteCount(stats);
      } else {
        rightItemList1.push(QuestionStatsRightItem(
          `RESPONSES`,
          null,
          EventStats.getChoicesCount(stats),
          'The number of unique responses submitted for the question.',
          <i className="material-icons" style={{fontSize: 36, color: '#f3c300'}}>poll</i>,
        ));
      }

      const rightItemList2 = [];

      const commenterCount = EventStats.getCommenterCount(stats);
      rightItemList2.push(QuestionStatsRightItem(
        `COMMENTS to Responses`,
        commenterCount,
        EventStats.getCommentCount(stats),
        'All user comments to responses.',
        <i className="material-icons" style={{fontSize: 36, color: '#3391f8'}}>forum</i>,
      ));
      
      const voterCount = EventStats.getTotalVoterCount(stats);
      rightItemList2.push(QuestionStatsRightItem(
        `VOTES`,
        voterCount,
        EventStats.getCommentVoteCount(stats) + voteCount,
        'All user votes on responses and/or comments.',
        <span>
          <i className="material-icons" style={{fontSize: 36, color: '#49a5b3'}}>thumb_up_alt</i>
          <i className="material-icons" style={{fontSize: 36, color: '#ff8c8e'}}>thumb_down_alt</i>
        </span>,
      ));

      return (
        <div className={ `row no-gutters ${questionStatsCardClass}` }>
          <div className="col px-3 pt-3">
            { (viewerCount === null || viewerCount === undefined)
              ? <QuestionStatsItem leftStats={ leftItem2 } rightStatsList={ rightItemList2 } />
              : <QuestionStatsItem leftStats={ leftItem1 } rightStatsList={ rightItemList1 } />
            }
            { (viewerCount === null || viewerCount === undefined) && (
              <div className="horizontal-container">
                <div className="horizontal-divider" />
              </div>
            ) }

            { (viewerCount === null || viewerCount === undefined)
              ? <QuestionStatsItem leftStats={ leftItem1 } rightStatsList={ rightItemList1 } />
              : <QuestionStatsItem leftStats={ leftItem2 } rightStatsList={ rightItemList2 } />
            }
          </div>
        </div>
      );
    }
  }
}

const QuestionStatsLeftItem = (icon, title, count, tooltip) => ({
  getIcon () {
    return icon;
  },

  getTitle () {
    return title;
  },

  getCount () {
    if (count === undefined || count === null) {
      return 'NA';
    } else {
      return count;
    }
  },

  isValid () {
    return !(count === undefined || count === null);
  },

  getTooltip () {
    return tooltip;
  }

});

const QuestionStatsRightItem = (title, subtitle, count, tooltip, icon) => ({
  getIcon () {
    return icon;
  },
  
  getTitle () {
    return title;
  },

  getSubtitle () {
    if (subtitle === null || subtitle === undefined) {
      return null;
    } else {
      return `By ${subtitle} ${subtitle !== 1 ? 'people' : 'person'}`;
    }
  },

  getCount () {
    if (count === undefined || count === null) {
      return 'NA';
    } else {
      return count;
    }
  },

  getTooltip () {
    return tooltip;
  }
});

/**
 * @typedef {Object} QuestionStatsLeftItem
 * @property {function<object>} getIcon
 * @property {function<string>} getTitle
 * @property {function<number>} getCount
 * @property {function<boolean>} isValid
 * @property {function<string>} getTooltip
 */

/**
 * @typedef {Object} QuestionStatsRightItem
 * @property {function<string>} getTitle
 * @property {function<string>} getSubtitle
 * @property {function<number>} getCount
 * @property {function<string>} getTooltip
 */

/**
 * @param {QuestionStatsLeftItem} leftStats
 * @param {Array<QuestionStatsRightItem>} rightStatsList
 */
const QuestionStatsItem = ({ leftStats, rightStatsList }) => (
  !!leftStats.isValid()
    ? (
      <div className="row no-gutters question-stats-item">
        <div className="col-5" style={{borderRight: "1px solid #6a6a6a"}}>
          <Tooltip tooltip={ leftStats.getTooltip() }>
            <div className="h-100 d-flex pt-4">
              <div className="row no-gutters w-100 align-items-start">
                <div className="col-4">
                </div>
                <div className="col-2">
                  <div className="m-auto h-100">
                    <img
                      src={ leftStats.getIcon() }
                      alt={ leftStats.getTitle() }
                    />
                  </div>
                </div>
                <div className="col-4">
                  <div className="item-title col text-center">{ leftStats.getTitle() }</div>
                  <div className="item-count col text-center">{ leftStats.getCount() }</div>
                </div>
              </div>
            </div>
          </Tooltip>
        </div>

        <div className={ `col pl-5 ${rightStatsList.length === 1 ? 'm-auto' : ''}` }>
          { rightStatsList.map(stat => (
            <Tooltip key={ stat.getTitle() } tooltip={ stat.getTooltip() }>
              <div
                className={ `row no-gutters right-item d-flex align-items-center
          ${rightStatsList.length === 1 ? 'my-auto h-100' : ''}` }>
                <div className="col-2" style={{minWidth:50, textAlign: 'center'}}>
                    {stat.getIcon()}
                </div>
                <div className="col-2 item-count pr-3 text-right">
                  <div>
                    { stat.getCount() }
                  </div>
                </div>
                <div className="col-8">
                  <div className={ `item-title text-left ${!stat.getSubtitle() && 'h-100 d-flex'}` }>
                    <div className={ `${!stat.getSubtitle() && 'my-auto'}` }>{ stat.getTitle() }</div>
                  </div>
                  { !!stat.getSubtitle() && (
                    <div className="item-subtitle text-left">
                      <div>{ stat.getSubtitle() }</div>
                    </div>
                  )
                  }
                </div>
              </div>
            </Tooltip>
          )) }
        </div>
      </div>
    )
    : (
      <div className="row no-gutters question-stats-item">
        <div className="col-12">
          <Tooltip tooltip={ leftStats.getTooltip() }>
            <div className="h-100 d-flex">
              <div className="row no-gutters w-100 align-items-start">
                <div className="col-2">
                  <div className="m-auto h-100">
                    <img
                      src={ leftStats.getIcon() }
                      alt={ leftStats.getTitle() }
                    />
                  </div>
                </div>
                <div className="col-10">
                  <EmptyState
                    title="Insufficient Information"
                    message="There is not enough information to accurately determine the Viewer count."
                  />
                </div>
              </div>
            </div>
          </Tooltip>
        </div>
      </div>
    )
);

const mapStateToProps = state => {
  const user = state.user.user;
  return {
    user: user,
    loading: state.event.statsLoading,
    error: state.event.statsError
  };
};

export default connect(mapStateToProps, {
  fetchEventDetails,
  fetchResponses
})(QuestionStats);
